'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function DataforceTranscriptionCaseStudy() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(43,98,239,0.06), transparent, rgb(8,11,18))' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <Link href="/case-studies" className="inline-flex items-center gap-2 text-cobalt text-sm font-medium mb-8 hover:text-cobalt-glow transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Case Studies
          </Link>
          <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">AI Data</span>
          <h1 className="font-display font-bold text-4xl lg:text-5xl text-silver-text leading-tight mb-6 max-w-4xl">
            DataForce Multilingual Transcription Support
          </h1>
          <p className="text-lg text-silver-muted max-w-2xl leading-relaxed mb-10">
            25-language transcription support for AI data operations - reviewing and correcting AI-generated transcriptions with trained reviewers, client-tool integration, and Bangalore-led delivery management.
          </p>
          <div className="flex flex-wrap gap-6">
            {[
              { value: '25', label: 'Languages' },
              { value: '10 per lang', label: 'Reviewer Team' },
              { value: 'Ongoing', label: 'Engagement' },
              { value: '8 Stages', label: 'QA Process' },
            ]?.map((stat) => (
              <div key={stat?.label} className="glow-card rounded-xl px-6 py-4 text-center min-w-[120px]">
                <div className="text-xl font-bold text-cobalt-glow">{stat?.value}</div>
                <div className="text-xs text-silver-muted mt-1">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">

              {/* Background */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">01</span>
                  About the Client
                </h2>
                <p className="text-silver-muted leading-relaxed mb-4">
                  DataForce by TransPerfect is a leading data solutions provider specializing in collecting, annotating, and preparing high-quality data for AI and machine learning models. Their programs require multilingual review and correction of AI-generated transcriptions across a broad language set.
                </p>
                <p className="text-silver-muted leading-relaxed">
                  The project required trained reviewers capable of using proprietary client tools, with dependable QA governance and experienced project management to ensure consistent quality across 25 languages.
                </p>
              </div>

              {/* The Work */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">02</span>
                  The Work
                </h2>
                <p className="text-silver-muted leading-relaxed mb-5">
                  Into23 provided teams of 10 highly-trained transcribers per language, all trained on DataForce's proprietary tools and passing strict quality assurance tests before beginning live transcription work.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Selling Unit', desc: 'Audio minutes - scalable billing aligned to project volume' },
                    { title: 'Team Structure', desc: '10 trained transcribers per language, all QA-tested' },
                    { title: 'Tool Integration', desc: 'Proprietary DataForce client tools for seamless workflow integration' },
                    { title: 'Management', desc: 'Experienced Bangalore-based team for timely delivery and scalable resources' },
                  ]?.map((item) => (
                    <div key={item?.title} className="glow-card rounded-xl p-5">
                      <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                      <p className="text-xs text-silver-muted">{item?.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 8-Stage Process */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">03</span>
                  8-Stage Transcription Process
                </h2>
                <div className="space-y-3">
                  {[
                    { step: '1', title: 'Project Initiation', desc: 'File receipt and analysis - understanding audio content, language, and quality requirements.' },
                    { step: '2', title: 'File Processing', desc: 'Formal check and preparation of audio/video files for transcription workflow.' },
                    { step: '3', title: 'AI Transcription', desc: 'Auto-generation of initial transcription drafts using AI tools.' },
                    { step: '4', title: 'First Pass Review', desc: 'Native expert review of AI-generated transcriptions for linguistic and cultural accuracy.' },
                    { step: '5', title: 'Second Pass Review', desc: 'Technical review for consistency, formatting, and adherence to client guidelines.' },
                    { step: '6', title: 'Final QA', desc: 'Accuracy check against source audio to verify all content is correctly transcribed.' },
                    { step: '7', title: 'Final Formatting', desc: 'Project manager formats output files to client specifications.' },
                    { step: '8', title: 'Client Delivery', desc: 'Secure file transfer in required output formats.' },
                  ]?.map((item) => (
                    <div key={item?.step} className="flex gap-4 p-4 glow-card rounded-xl">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-xs font-bold">{item?.step}</div>
                      <div>
                        <h3 className="font-semibold text-silver-text text-sm mb-0.5">{item?.title}</h3>
                        <p className="text-xs text-silver-muted leading-relaxed">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">04</span>
                  Language Coverage
                </h2>
                <div className="space-y-4">
                  {[
                    { group: 'East Asian', langs: ['Chinese (Mandarin)', 'Chinese (Cantonese)', 'Japanese', 'Korean'] },
                    { group: 'Southeast Asian', langs: ['Vietnamese', 'Thai', 'Khmer', 'Indonesian'] },
                    { group: 'South Asian', langs: ['Bengali', 'Marathi', 'Hindi', 'Tamil', 'Telugu', 'Urdu', 'Sinhala'] },
                    { group: 'European', langs: ['French', 'Spanish', 'German', 'Italian', 'Portuguese', 'Russian'] },
                    { group: 'Other', langs: ['Swahili', 'Uzbek', 'Arabic', 'Turkish'] },
                  ]?.map((group) => (
                    <div key={group?.group}>
                      <h3 className="text-xs font-mono text-cobalt/70 uppercase tracking-wider mb-2">{group?.group}</h3>
                      <div className="flex flex-wrap gap-2">
                        {group?.langs?.map((lang) => (
                          <span key={lang} className="px-3 py-1.5 rounded-lg bg-cobalt/10 border border-cobalt/20 text-cobalt-glow text-xs font-mono">{lang}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Formats */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">05</span>
                  Supported Formats
                </h2>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="glow-card rounded-xl p-5">
                    <h3 className="font-semibold text-silver-text text-sm mb-3">Audio Input</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {['MP3', 'WAV', 'AAC', 'M4A']?.map((f) => <span key={f} className="px-2 py-1 bg-steel/20 rounded text-xs font-mono text-silver-muted">{f}</span>)}
                    </div>
                  </div>
                  <div className="glow-card rounded-xl p-5">
                    <h3 className="font-semibold text-silver-text text-sm mb-3">Video Input</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {['MP4', 'MOV', 'AVI', 'WMV']?.map((f) => <span key={f} className="px-2 py-1 bg-steel/20 rounded text-xs font-mono text-silver-muted">{f}</span>)}
                    </div>
                  </div>
                  <div className="glow-card rounded-xl p-5">
                    <h3 className="font-semibold text-silver-text text-sm mb-3">Output Formats</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {['.docx', '.txt', '.srt', '.vtt', '.xlsx', '.pdf']?.map((f) => <span key={f} className="px-2 py-1 bg-steel/20 rounded text-xs font-mono text-silver-muted">{f}</span>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-4">Project Details</h3>
                <dl className="space-y-3 text-sm">
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Client</dt><dd className="text-silver-text">DataForce by TransPerfect</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Industry</dt><dd className="text-silver-text">AI Data / Machine Learning</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Languages</dt><dd className="text-silver-text">25 languages</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Engagement Type</dt><dd className="text-silver-text">Ongoing, multi-month</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Billing Unit</dt><dd className="text-silver-text">Audio minutes</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Management Base</dt><dd className="text-silver-text">Bangalore, India</dd></div>
                </dl>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Key Capabilities Used</h3>
                <ul className="space-y-2 text-sm text-silver-muted">
                  {['AI transcription review', 'Native expert reviewers', 'Client tool integration', 'Multi-stage QA', 'Subtitle formatting', 'Scalable team structure', 'Bangalore delivery management']?.map((cap) => (
                    <li key={cap} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-cobalt-glow flex-shrink-0" />
                      {cap}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Need AI Data Support?</h3>
                <p className="text-sm text-silver-muted mb-4">Talk to our team about multilingual transcription programs.</p>
                <Link href="/get-a-quote">
                  <span className="block text-center px-4 py-3 bg-cobalt hover:bg-cobalt-glow text-white text-sm font-semibold rounded-lg transition-all">
                    Get a Quote
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
